import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { apiOkr } from 'app/config/axios-interceptor';
import { IField, defaultValue } from 'app/shared/model/field.model';
import { EntityState, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import qs from 'qs';

const initialState: EntityState<IField> = {
  loading: false,
  errorMessage: null,
  entities: null,
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/okr/checkIn';
// Actions
type IKrNeedCheckIn = {
  planPeriodId?: string;
  reportScheduleId?: string;
  partyId?: string;
  checkinType?: number;
};

export const getEntities = createAsyncThunk(
  'checkIn/fetch_entity_list',
  async ({ partyId, planPeriodId, reportScheduleId, checkinType }: IKrNeedCheckIn, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/kr-need-checkin`, {
        params: {
          partyId,
          planPeriodId,
          reportScheduleId,
          checkinType,
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

type IOrgCheckIn = {
  searchText?: string;
  accountableId: string;
  checkinStatus?: number;
  checkInPeriod?: number;
  projectId?: string;
  fieldValue?: number;
  krAccountableId?: string;
  workTypeId?: string;
  workStatus?: number;
};

export const getCheckInByAccountableId = createAsyncThunk(
  'checkIn/fetch_item_checkin_accountable_id',
  async ({
    accountableId,
    searchText,
    checkinStatus,
    checkInPeriod,
    krAccountableId,
    fieldValue,
    projectId,
    workStatus,
    workTypeId,
  }: IOrgCheckIn) => {
    return apiOkr.get<any[]>(`${apiUrl}/kr-checkin-list/${accountableId}`, {
      params: {
        searchText,
        checkinStatus,
        checkInPeriod,
        krAccountableId,
        fieldValue,
        projectId,
        workStatus,
        workTypeId,
      },
      paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  },
);

export const getKrConfirmComplete = createAsyncThunk(
  'checkIn/fetch_list_kr_confirm_complete',
  ({ partyId, planPeriodId, reportScheduleId }: IKrNeedCheckIn, { rejectWithValue }) => {
    const response = apiOkr
      .get<any>(`${apiUrl}/submitted-info-mid-year`, {
        params: {
          partyId,
          planPeriodId,
          reportScheduleId,
        },
      })
      .catch(err => {
        return rejectWithValue(err);
      });
    return response;
  },
);

export const getKrConfirmCompleteQuarter = createAsyncThunk(
  'checkIn/fetch_list_kr_confirm_complete_quarter',
  (partyId: string, { rejectWithValue }) => {
    const response = apiOkr.get<any>(`${apiUrl}/submitted-info-quarter/${partyId}`).catch(err => {
      return rejectWithValue(err);
    });
    return response;
  },
);

export const getKrConfirmCompleteHalfMonth = createAsyncThunk(
  'checkIn/fetch_list_kr_confirm_complete_half_month',
  ({ partyId }: IKrNeedCheckIn, { rejectWithValue }) => {
    const response = apiOkr.get<any>(`${apiUrl}/submitted-info-half-month/${partyId}`).catch(err => {
      return rejectWithValue(err);
    });
    return response;
  },
);

export const getKrConfirmCompleteMonth = createAsyncThunk(
  'checkIn/fetch_list_kr_confirm_complete_month',
  ({ partyId }: IKrNeedCheckIn, { rejectWithValue }) => {
    const response = apiOkr.get<any>(`${apiUrl}/submitted-info-month/${partyId}`).catch(err => {
      return rejectWithValue(err);
    });
    return response;
  },
);

export const saveComplete = createAsyncThunk('checkIn/save_complete', async (entity: any, thunkAPI) => {
  const concatEntity = { ...entity, SuccessNote: '.', FailNote: '.', AdjustmentNote: '.', Issue: '.', Solution: '.' };
  const response = await apiOkr.post<any>(`${apiUrl}/submitted-mid-year`, concatEntity).catch(err => {
    return thunkAPI.rejectWithValue(err);
  });
  return response;
});

export const saveCompleteQuarter = createAsyncThunk('checkIn/save_complete_quarter', async (entity: any, thunkAPI) => {
  const response = await apiOkr.post<any>(`${apiUrl}/submitted-quarter`, entity).catch(err => {
    return thunkAPI.rejectWithValue(err);
  });
  return response;
});

export const saveCompleteMonth = createAsyncThunk('checkIn/save_complete_month', async (entity: any, thunkAPI) => {
  const response = await apiOkr.post<any>(`${apiUrl}/submitted-month`, entity).catch(err => {
    return thunkAPI.rejectWithValue(err);
  });
  return response;
});

export const saveCompleteHalfMonth = createAsyncThunk('checkIn/save_complete_half_month', async (entity: any, thunkAPI) => {
  const response = await apiOkr.post<any>(`${apiUrl}/submitted-half-month`, entity).catch(err => {
    return thunkAPI.rejectWithValue(err);
  });
  return response;
});

export const saveDraft = createAsyncThunk('checkIn/save_draft', async (entity: any, thunkAPI) => {
  const response = await apiOkr.post<any>(`${apiUrl}/submitted-draft-mid-year`, entity).catch(err => {
    return thunkAPI.rejectWithValue(err);
  });
  return response;
});

type ICheckInKrMidYear = {
  planPeriodId?: string;
  krId?: string;
};

export const getKrQualCheckInMidYear = createAsyncThunk(
  'checkIn/fetch_KrQual_list',
  async ({ krId, planPeriodId }: ICheckInKrMidYear, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/checkin-qualitative-mid-year-view`, {
        params: {
          krId,
          planPeriodId,
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);
export const getKrQualCheckInQuarter = createAsyncThunk(
  'checkIn/fetch_KrQual_first_list',
  async (krId: string, { rejectWithValue }) => {
    const response = await apiOkr.get<any>(`${apiUrl}/checkin-quarter-qualitative-view/${krId}`).catch(error => {
      return rejectWithValue(error);
    });
    return response;
  },
  { serializeError: serializeAxiosError },
);

export const getKrQualCheckInMonth = createAsyncThunk(
  'checkIn/fetch_KrQual_month_list',
  async (krId: string, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/checkin-qualitative-month/${krId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

export const getKrQualCheckInHalfMonth = createAsyncThunk(
  'checkIn/fetch_KrQual_half_month_list',
  async ({ krId, planPeriodId }: ICheckInKrMidYear, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/checkin-half-month-qualitative`, {
        params: {
          krId,
          planPeriodId,
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQualMonth = createAsyncThunk(
  'checkIn/check_in_KrQual_month',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/kr-qualitative`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQualDrafftYear = createAsyncThunk(
  'checkIn/check_in_KrQual_month',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/kr-qualitative`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQualQuarter = createAsyncThunk(
  'checkIn/check_in_KrQual_quarter',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/kr-qualitative-quarter`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const getKrQuantiCheckInMidYear = createAsyncThunk(
  'checkIn/fetch_KrQuanti_list',
  async ({ krId, planPeriodId }: ICheckInKrMidYear, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/checkin-quantitative-mid-year-view`, {
        params: {
          krId,
          planPeriodId,
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);
export const getKrQuantiCheckInQuarter = createAsyncThunk(
  'checkIn/fetch_KrQuanti_first_list',
  async (krId: string, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/checkin-quarter-quantitative-view/${krId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

export const getKrQuantiCheckInMonth = createAsyncThunk(
  'checkIn/fetch_KrQuanti_month_list',
  async (krId: string, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/checkin-quantitative-month/${krId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

export const getKrQuantiCheckInHalfMonth = createAsyncThunk(
  'checkIn/fetch_KrQuanti_half_month_list',
  async ({ krId, planPeriodId }: ICheckInKrMidYear, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/checkin-half-month-quantitative`, {
        params: {
          krId,
          planPeriodId,
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQuantiMonth = createAsyncThunk(
  'checkIn/check_in_KrQuanti_month',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/kr-quantitative`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const latchCheckInKrQuanti = createAsyncThunk(
  'checkIn/check_in_KrQuanti_month',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.put<any>(`${apiUrl}/kr-quantitative`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQuantiQuarter = createAsyncThunk(
  'checkIn/check_in_KrQuant_quarter',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/kr-quantitative-quarter`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const getCurrentCheckInType = createAsyncThunk('checkIn/fetch_current_kr_checkIn_type', async () => {
  return apiOkr.get<any>(`${apiUrl}/curr-checkin-info`);
});

export const checkInQuarterQuantitative = createAsyncThunk(
  'key-result-quantitative/check_in_key_result_quanti_quarter',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/checkin-quarter-quantitative-view`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInQuarterQualitative = createAsyncThunk(
  'key-result-qualitative/check_in_key_result_qual_quarter',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/checkin-quarter-qualitative-view`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInQuantitativeMidYear = createAsyncThunk(
  'key-result-quantitative/check_in_key_result_quanti_mid_year',
  async (payload: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/quantitative-mid-year`, payload);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInQuantitativeMidYearAdjustment = createAsyncThunk(
  'key-result-quantitative/check_in_key_result_quanti_mid_year_adjustment',
  async (payload: any, thunkAPI) => {
    const payloadAdjustment = {
      krId: payload.krId,
      subs: payload.adjustment.subs,
      startDate: payload.adjustment.startDate,
      dueDate: payload.adjustment.dueDate,
      reason: payload.adjustment.reason,
      issue: payload.adjustment.issue,
      solution: payload.adjustment.solution,
      krAdjustmentNote: payload.adjustment.krAdjustmentNote,
    }
    const result = await apiOkr.post<any>('/api/okr/year-planning/adjustment-mid-year-quantitative', payloadAdjustment);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInQualititativeMidYear = createAsyncThunk(
  'key-result-qualitative/check_in_key_result_qual_mid_year',
  async (payload: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/qualitative-mid-year`, payload);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInQualititativeMidYearAdjustment = createAsyncThunk(
  'key-result-qualitative/check_in_key_result_qual_mid_year_adjustment',
  async (payload: any, thunkAPI) => {
    const payloadAdjustment = {
      krId: payload.krId,
      subs: payload.adjustment.subs,
      reason: payload.adjustment.adjustmentReason,
      issue: payload.adjustment.adjustmentIssue,
      solution: payload.adjustment.adjustmentSolution,
      krAdjustmentNote: payload.adjustment.adjustmentNote,
    }
    
    const result = await apiOkr.post<any>(`/api/okr/year-planning/adjustment-mid-year-qualitative`, payloadAdjustment);
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const CheckInSlice = createEntitySlice({
  name: 'checkIn',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getKrQuantiCheckInMidYear.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getKrQuantiCheckInQuarter.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getKrQuantiCheckInHalfMonth.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getKrQuantiCheckInMonth.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getKrQualCheckInQuarter.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getKrQualCheckInHalfMonth.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getKrQualCheckInMonth.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getKrQualCheckInMidYear.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getKrConfirmCompleteQuarter.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(getKrConfirmComplete.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(getKrConfirmCompleteHalfMonth.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(getKrConfirmCompleteMonth.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addMatcher(
        isPending(
          getKrQualCheckInMidYear,
          getKrQuantiCheckInMidYear,
          getKrQualCheckInQuarter,
          getKrQuantiCheckInQuarter,
          getKrQualCheckInHalfMonth,
          getKrQuantiCheckInHalfMonth,
          getKrQualCheckInMonth,
          getKrQuantiCheckInMonth,
        ),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.loading = true;
          state.entity = {};
        },
      )
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(getCheckInByAccountableId), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: data.length,
        };
      })
      .addMatcher(isFulfilled(saveComplete, saveCompleteMonth, saveCompleteHalfMonth, saveCompleteQuarter), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(checkInKrQualMonth, checkInKrQualQuarter, checkInKrQuantiMonth, checkInKrQuantiQuarter, checkInQuantitativeMidYear, checkInQualititativeMidYear, checkInQualititativeMidYearAdjustment), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isPending(saveComplete, saveCompleteMonth, saveCompleteHalfMonth, saveDraft, saveCompleteQuarter), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(
        isPending(getKrConfirmComplete, getKrConfirmCompleteHalfMonth, getKrConfirmCompleteMonth, getKrConfirmCompleteQuarter),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.loading = true;
        },
      )
      .addMatcher(isPending(getEntities, getCheckInByAccountableId), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(checkInKrQualMonth, checkInKrQualQuarter, checkInKrQuantiMonth, checkInKrQuantiQuarter, checkInQuantitativeMidYear, checkInQualititativeMidYear, checkInQualititativeMidYearAdjustment), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = CheckInSlice.actions;

// Reducer
export default CheckInSlice.reducer;
